import { Link } from "react-router-dom";
import {
    Globe, AppWindow, Palette, MessageSquare, Megaphone, Bot,
    CheckCircle2, Clock, Zap, ArrowRight, Sparkles, Quote
} from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { HeroMockup } from "@/components/HeroMockup";
import { Section, SectionHeader, PrimaryButton, SecondaryButton } from "@/components/Section";
import { ContactForm } from "@/components/ContactForm";
import { SocialPlanCard } from "@/components/SocialPlanCard";
import { SEO } from "@/components/SEO";
import { site, services, packages, portfolio, process as steps, faqs } from "@/config/site";

const icons = { Globe, AppWindow, Palette, MessageSquare, Megaphone, Bot };

export default function Home() {
    return (
        <>
            <SEO
                title="Szymkas Agency – strony internetowe, branding i marketing dla firm"
                description="Tworzymy strony internetowe, aplikacje, branding, social media i marketing wspierany przez AI. Zbuduj profesjonalny wizerunek swojej firmy w sieci."
            />

            {/* HERO */}
            <section className="relative bg-[#06111F] text-white overflow-hidden pt-28 pb-20 md:pt-36 md:pb-28">
                <div className="absolute inset-0 bg-grid-faint opacity-60" aria-hidden />
                <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full bg-[#14B8A6]/10 blur-3xl" aria-hidden />
                <div className="absolute -bottom-32 -left-32 w-[400px] h-[400px] rounded-full bg-[#0EA5A5]/10 blur-3xl" aria-hidden />

                <div className="relative max-w-7xl mx-auto px-5 md:px-8 lg:px-12 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
                    <div className="reveal-up">
                        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-xs font-medium text-[#AAB6C5] mb-6">
                            <Sparkles className="w-3.5 h-3.5 text-[#14B8A6]" />
                            Strony WWW • Aplikacje • Branding • Social Media • Marketing AI
                        </div>
                        <h1 className="font-display font-extrabold text-[38px] sm:text-[48px] md:text-[58px] lg:text-[68px] leading-[1.05] tracking-tight">
                            Budujemy marki,<br />które{" "}
                            <span className="bg-gradient-to-r from-[#14B8A6] to-[#5EEAD4] bg-clip-text text-transparent">
                                rosną w sieci
                            </span>
                            .
                        </h1>
                        <p className="mt-6 text-base md:text-lg text-[#AAB6C5] max-w-xl leading-relaxed">
                            Tworzymy strony internetowe, aplikacje, identyfikacje wizualne i marketing,
                            które pomagają firmom budować zaufanie, docierać do nowych klientów i rozwijać sprzedaż online.
                        </p>
                        <div className="mt-8 flex flex-col sm:flex-row gap-3">
                            <PrimaryButton to="/kontakt" testid="hero-cta-primary">
                                Umów bezpłatną konsultację <ArrowRight className="w-4 h-4" />
                            </PrimaryButton>
                            <SecondaryButton to="/pakiety" light testid="hero-cta-secondary">
                                Zobacz pakiety
                            </SecondaryButton>
                        </div>
                        <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-5">
                            {[
                                { icon: CheckCircle2, t: "Przejrzysta wycena" },
                                { icon: Clock, t: "Sprawny proces realizacji" },
                                { icon: Zap, t: "Technologia wspierana przez AI" },
                            ].map(({ icon: Ic, t }) => (
                                <div key={t} className="flex items-center gap-2 text-sm text-[#AAB6C5]">
                                    <Ic className="w-4 h-4 text-[#14B8A6] flex-shrink-0" />
                                    <span>{t}</span>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="reveal-up" style={{ animationDelay: "0.15s" }}>
                        <HeroMockup />
                    </div>
                </div>
            </section>

            {/* Services strip */}
            <div className="bg-[#0F172A] border-y border-white/5 text-[#AAB6C5] text-xs md:text-sm">
                <div className="max-w-7xl mx-auto px-5 md:px-8 lg:px-12 py-4 flex flex-wrap items-center justify-center gap-x-8 gap-y-2">
                    {["Strony WWW", "Aplikacje", "Branding", "Social Media", "Marketing", "Automatyzacje AI"].map((s) => (
                        <span key={s} className="flex items-center gap-2">
                            <span className="w-1.5 h-1.5 rounded-full bg-[#14B8A6]" /> {s}
                        </span>
                    ))}
                </div>
            </div>

            {/* Internet scale section */}
            <Section variant="light">
                <div className="grid lg:grid-cols-2 gap-12 items-center">
                    <div>
                        <div className="text-xs font-semibold tracking-widest uppercase text-[#14B8A6] mb-3">Skala internetu</div>
                        <h2 className="font-display font-bold text-[30px] md:text-[40px] lg:text-[48px] leading-tight tracking-tight">
                            Internet daje Twojej firmie skalę.
                        </h2>
                        <p className="mt-5 text-base md:text-lg text-[#0B1220]/70 leading-relaxed">
                            Działając wyłącznie lokalnie, możesz docierać do klientów z jednej ulicy, dzielnicy lub miasta.
                            Profesjonalna obecność w internecie pozwala prezentować ofertę odbiorcom z całej Polski — przez całą dobę.
                        </p>
                        <blockquote className="mt-8 border-l-4 border-[#14B8A6] pl-5 py-2 text-lg md:text-xl font-display font-semibold text-[#0B1220]/90 italic">
                            <Quote className="w-5 h-5 text-[#14B8A6] mb-2" />
                            Strona internetowa nie jest tylko wizytówką. To narzędzie, które może pracować dla Twojej firmy przez 24 godziny na dobę.
                        </blockquote>
                    </div>
                    <div className="relative">
                        <div className="bg-gradient-to-br from-[#F6F8FA] to-white rounded-[20px] p-6 md:p-8 border border-[#E5E7EB] shadow-sm">
                            <div className="flex items-center justify-between mb-6">
                                <span className="px-3 py-1 rounded-full bg-[#14B8A6]/10 text-[#0EA5A5] text-xs font-semibold">Twoja firma online</span>
                                <span className="text-xs text-[#0B1220]/40">24/7</span>
                            </div>
                            <div className="space-y-3">
                                {["Warszawa", "Kraków", "Poznań", "Gdańsk", "Wrocław", "Łódź", "Lublin", "Katowice"].map((city, i) => (
                                    <div key={city} className="flex items-center gap-3">
                                        <span className="w-2 h-2 rounded-full bg-[#14B8A6]" />
                                        <span className="text-sm font-medium text-[#0B1220]">{city}</span>
                                        <div className="flex-1 h-1.5 rounded-full bg-[#E5E7EB] overflow-hidden">
                                            <div className="h-full bg-gradient-to-r from-[#14B8A6] to-[#0EA5A5]" style={{ width: `${70 + i * 3}%` }} />
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <div className="mt-6 pt-6 border-t border-[#E5E7EB] grid grid-cols-3 gap-4 text-center">
                                <div><div className="text-2xl font-display font-bold text-[#0B1220]">24/7</div><div className="text-xs text-[#0B1220]/60">dostępność</div></div>
                                <div><div className="text-2xl font-display font-bold text-[#14B8A6]">PL</div><div className="text-xs text-[#0B1220]/60">cała Polska</div></div>
                                <div><div className="text-2xl font-display font-bold text-[#0B1220]">∞</div><div className="text-xs text-[#0B1220]/60">odbiorców</div></div>
                            </div>
                        </div>
                    </div>
                </div>
            </Section>

            {/* Services */}
            <Section variant="gray" id="uslugi">
                <SectionHeader
                    eyebrow="Usługi"
                    title="Wszystko, czego potrzebuje Twoja marka w internecie"
                    subtitle="Łączymy technologię, design i marketing w jeden spójny system."
                />
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                    {services.map((s) => {
                        const Icon = icons[s.icon] || Globe;
                        return (
                            <Link
                                key={s.slug}
                                to={`/${s.slug}`}
                                data-testid={`service-card-${s.slug}`}
                                className="group rounded-[20px] bg-white border border-[#E5E7EB] p-7 md:p-8 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_24px_50px_-20px_rgba(20,184,166,0.25)] hover:border-[#14B8A6]/40"
                            >
                                <div className="w-12 h-12 rounded-xl bg-[#14B8A6]/10 flex items-center justify-center mb-5 group-hover:bg-[#14B8A6] group-hover:text-white transition-colors text-[#14B8A6]">
                                    <Icon className="w-6 h-6" />
                                </div>
                                <h3 className="font-display font-semibold text-xl md:text-2xl mb-3 text-[#0B1220]">{s.title}</h3>
                                <p className="text-[#0B1220]/70 text-[15px] leading-relaxed">{s.short}</p>
                                <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-[#14B8A6] group-hover:gap-2.5 transition-all">
                                    Dowiedz się więcej <ArrowRight className="w-4 h-4" />
                                </span>
                            </Link>
                        );
                    })}
                </div>
            </Section>

            {/* Advantages */}
            <Section variant="dark">
                <SectionHeader
                    eyebrow="Dlaczego my"
                    title="Technologia przyspiesza. Strategia nadaje kierunek."
                    subtitle="Wykorzystujemy nowoczesne narzędzia, aby pracować sprawniej, ale najważniejsze decyzje zawsze wynikają ze strategii i potrzeb Twojej firmy."
                    light
                />
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                    {[
                        { t: "Szybsza realizacja", d: "Automatyzujemy powtarzalne procesy i poświęcamy więcej czasu na decyzje wpływające na jakość projektu." },
                        { t: "Efektywna cena", d: "Nie ponosisz kosztów rozbudowanej struktury dużej agencji. Płacisz za konkretną realizację i rzeczywistą wartość." },
                        { t: "Indywidualne podejście", d: "Każdy projekt dopasowujemy do branży, odbiorców, oferty oraz celów firmy." },
                        { t: "Bezpośredni kontakt", d: "Rozmawiasz bezpośrednio z osobą odpowiedzialną za strategię i realizację projektu." },
                    ].map((a, i) => (
                        <div key={a.t} className="rounded-[20px] bg-white/[0.03] border border-white/10 p-6 md:p-7 hover:bg-white/[0.06] hover:border-[#14B8A6]/40 transition-all" data-testid={`advantage-${i}`}>
                            <div className="text-[#14B8A6] font-display font-bold text-2xl mb-3">0{i + 1}</div>
                            <h3 className="font-display font-semibold text-lg md:text-xl text-white mb-2">{a.t}</h3>
                            <p className="text-[#AAB6C5] text-[14px] leading-relaxed">{a.d}</p>
                        </div>
                    ))}
                </div>
                <div className="mt-10 max-w-3xl mx-auto rounded-[16px] border border-[#14B8A6]/30 bg-[#14B8A6]/5 px-6 py-5 text-center text-[#AAB6C5] text-sm md:text-base">
                    <span className="text-[#14B8A6] font-semibold">AI przyspiesza pracę.</span> Ostateczna strategia, decyzje i kontrola jakości pozostają po naszej stronie.
                </div>
            </Section>

            {/* Packages */}
            <Section variant="light" id="pakiety">
                <SectionHeader
                    eyebrow="Pakiety"
                    title="Wybierz rozwiązanie dopasowane do etapu rozwoju Twojej firmy"
                    subtitle="Trzy ścieżki – jasny zakres, przewidywalna wycena."
                />
                <div className="grid lg:grid-cols-3 gap-6 md:gap-8 items-stretch">
                    {packages.map((p) => (
                        <div
                            key={p.slug}
                            data-testid={`package-card-${p.slug}`}
                            className={`relative rounded-[20px] p-7 md:p-8 flex flex-col ${
                                p.highlighted
                                    ? "border-2 border-[#14B8A6] bg-gradient-to-b from-white to-[#14B8A6]/[0.04] shadow-[0_30px_60px_-20px_rgba(20,184,166,0.35)] lg:scale-[1.03] z-10"
                                    : "border border-[#E5E7EB] bg-white"
                            }`}
                        >
                            {p.highlighted && (
                                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full bg-[#14B8A6] text-white text-xs font-semibold">
                                    {p.badge}
                                </div>
                            )}
                            <h3 className="font-display font-semibold text-2xl md:text-3xl text-[#0B1220]">{p.name}</h3>
                            <div className="mt-2 text-[#14B8A6] font-display font-bold text-2xl">{p.priceFrom}</div>
                            <p className="mt-3 text-sm text-[#0B1220]/70 leading-relaxed">{p.description}</p>
                            <ul className="mt-6 space-y-3 text-[14px] text-[#0B1220]/80 flex-1">
                                {p.features.map((f) => (
                                    <li key={f} className="flex gap-2.5">
                                        <CheckCircle2 className="w-4 h-4 text-[#14B8A6] flex-shrink-0 mt-0.5" />
                                        <span>{f}</span>
                                    </li>
                                ))}
                            </ul>
                            <Link
                                to={`/kontakt?package=${encodeURIComponent(p.name)}`}
                                data-testid={`package-cta-${p.slug}`}
                                className={`mt-7 inline-flex items-center justify-center px-5 py-3 rounded-[12px] font-semibold text-sm transition-all hover:-translate-y-0.5 ${
                                    p.highlighted
                                        ? "bg-[#14B8A6] hover:bg-[#0EA5A5] text-white shadow-[0_10px_24px_-8px_rgba(20,184,166,0.55)]"
                                        : "border border-[#0B1220]/15 text-[#0B1220] hover:border-[#14B8A6] hover:text-[#14B8A6]"
                                }`}
                            >
                                {p.cta}
                            </Link>
                        </div>
                    ))}
                </div>
                <div className="mt-10 text-center">
                    <p className="text-[#0B1220]/70 mb-4">Nie widzisz rozwiązania dla siebie? Przygotujemy indywidualny zakres i wycenę.</p>
                    <SecondaryButton to="/kontakt?package=Indywidualny" testid="packages-custom-cta">
                        Poproś o indywidualną wycenę
                    </SecondaryButton>
                </div>
                <SocialPlanCard testidPrefix="home-social-plan" />
            </Section>

            {/* Portfolio */}
            <Section variant="gray" id="realizacje">
                <SectionHeader
                    eyebrow="Realizacje"
                    title="Projektujemy rozwiązania, nie tylko ładne grafiki"
                    subtitle="Każda realizacja rozpoczyna się od poznania problemu, odbiorców i celu biznesowego."
                />
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                    {portfolio.map((c, i) => (
                        <Link
                            key={c.slug}
                            to="/realizacje"
                            data-testid={`portfolio-card-${c.slug}`}
                            className="group rounded-[20px] bg-white border border-[#E5E7EB] overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_24px_50px_-20px_rgba(20,184,166,0.25)]"
                        >
                            <div className="aspect-[16/10] bg-gradient-to-br from-[#0F172A] to-[#06111F] relative overflow-hidden">
                                <div className="absolute inset-0 bg-grid-faint opacity-50" />
                                <div className="absolute inset-0 flex items-center justify-center">
                                    <div className="w-[70%] aspect-video rounded-md bg-white/10 border border-white/20 backdrop-blur-sm p-4">
                                        <div className="h-1.5 w-1/2 bg-[#14B8A6] rounded-full mb-2" />
                                        <div className="h-1 w-3/4 bg-white/30 rounded mb-1" />
                                        <div className="h-1 w-2/3 bg-white/20 rounded mb-3" />
                                        <div className="grid grid-cols-3 gap-1.5 mt-auto">
                                            <div className="aspect-square rounded bg-[#14B8A6]/30" />
                                            <div className="aspect-square rounded bg-white/10" />
                                            <div className="aspect-square rounded bg-white/10" />
                                        </div>
                                    </div>
                                </div>
                                <div className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-[#14B8A6] text-white text-[10px] font-semibold uppercase tracking-wider">
                                    Case 0{i + 1}
                                </div>
                            </div>
                            <div className="p-6">
                                <div className="text-xs font-semibold text-[#14B8A6] uppercase tracking-wider mb-2">{c.category}</div>
                                <h3 className="font-display font-semibold text-xl text-[#0B1220] mb-2">{c.title}</h3>
                                <p className="text-sm text-[#0B1220]/65 leading-relaxed line-clamp-3">{c.solution}</p>
                                {c.concept && (
                                    <p className="mt-3 text-[11px] text-[#0B1220]/40 italic">
                                        Projekt koncepcyjny przygotowany na potrzeby portfolio.
                                    </p>
                                )}
                            </div>
                        </Link>
                    ))}
                </div>
            </Section>

            {/* Process */}
            <Section variant="light" id="proces">
                <SectionHeader
                    eyebrow="Proces"
                    title="Od pomysłu do publikacji — jasno i krok po kroku"
                />
                <div className="hidden md:block">
                    <div className="grid grid-cols-7 gap-4 relative">
                        <div className="absolute top-6 left-12 right-12 h-0.5 bg-gradient-to-r from-[#14B8A6]/30 via-[#14B8A6] to-[#14B8A6]/30" />
                        {steps.map((s) => (
                            <div key={s.n} className="relative text-center" data-testid={`process-step-${s.n}`}>
                                <div className="mx-auto w-12 h-12 rounded-full bg-white border-2 border-[#14B8A6] text-[#14B8A6] font-display font-bold text-lg flex items-center justify-center relative z-10">
                                    {s.n}
                                </div>
                                <h4 className="mt-4 font-display font-semibold text-sm text-[#0B1220]">{s.title}</h4>
                                <p className="mt-1.5 text-xs text-[#0B1220]/60 leading-relaxed">{s.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
                <ol className="md:hidden border-l-2 border-[#14B8A6]/30 pl-6 space-y-7">
                    {steps.map((s) => (
                        <li key={s.n} className="relative" data-testid={`process-step-m-${s.n}`}>
                            <span className="absolute -left-[34px] w-10 h-10 rounded-full bg-white border-2 border-[#14B8A6] text-[#14B8A6] font-display font-bold flex items-center justify-center">
                                {s.n}
                            </span>
                            <h4 className="font-display font-semibold text-base text-[#0B1220]">{s.title}</h4>
                            <p className="mt-1 text-sm text-[#0B1220]/70">{s.desc}</p>
                        </li>
                    ))}
                </ol>
            </Section>

            {/* Owner */}
            <Section variant="gray" id="o-nas">
                <div className="grid lg:grid-cols-5 gap-10 md:gap-14 items-center">
                    <div className="lg:col-span-2">
                        <div className="relative aspect-[4/5] max-w-sm mx-auto rounded-[20px] overflow-hidden border border-[#E5E7EB] bg-gradient-to-br from-[#0F172A] to-[#06111F] shadow-xl">
                            <div className="absolute inset-0 bg-grid-faint opacity-30" />
                            <img
                                src={site.ownerPhoto}
                                alt={`${site.ownerName} – ${site.ownerRole}`}
                                className="relative w-full h-full object-cover object-top"
                                loading="lazy"
                                data-testid="owner-photo-home"
                            />
                            <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between text-white">
                                <span className="text-xs font-display font-semibold">SA</span>
                                <span className="text-[10px] tracking-widest uppercase text-[#14B8A6]">Założyciel</span>
                            </div>
                        </div>
                    </div>
                    <div className="lg:col-span-3">
                        <div className="text-xs font-semibold tracking-widest uppercase text-[#14B8A6] mb-3">O właścicielu</div>
                        <h2 className="font-display font-bold text-[30px] md:text-[40px] lg:text-[48px] leading-tight tracking-tight">
                            Za {site.companyName} stoi konkretna osoba
                        </h2>
                        <p className="mt-5 text-base md:text-lg text-[#0B1220]/75 leading-relaxed">
                            Nazywam się {site.ownerName}. Pomagam firmom budować profesjonalny wizerunek i skutecznie działać w internecie.
                            Łączę projektowanie, technologię, marketing oraz nowoczesne narzędzia AI, aby realizować projekty sprawniej i efektywniej.
                        </p>
                        <p className="mt-4 text-base md:text-lg text-[#0B1220]/75 leading-relaxed">
                            Zależy mi na prostym procesie, jasnej komunikacji oraz rozwiązaniach, które wspierają realne cele biznesowe klienta.
                        </p>
                        <div className="mt-6">
                            <div className="font-display font-semibold text-[#0B1220]">{site.ownerName}</div>
                            <div className="text-sm text-[#0B1220]/60">{site.ownerRole}</div>
                        </div>
                        <div className="mt-7">
                            <PrimaryButton to="/kontakt" testid="owner-cta">Porozmawiajmy o Twojej firmie</PrimaryButton>
                        </div>
                    </div>
                </div>
            </Section>

            {/* Testimonials placeholder */}
            <Section variant="light">
                <SectionHeader eyebrow="Opinie" title="Co mówią klienci" />
                <div className="max-w-2xl mx-auto rounded-[20px] border border-dashed border-[#E5E7EB] bg-[#F6F8FA] p-8 md:p-10 text-center">
                    <Quote className="w-8 h-8 text-[#14B8A6] mx-auto mb-4" />
                    <p className="text-[#0B1220]/70 text-base md:text-lg leading-relaxed">
                        Pierwsze realizacje {site.companyName} są obecnie w przygotowaniu. Wkrótce pojawią się tutaj opinie i historie klientów.
                    </p>
                </div>
            </Section>

            {/* FAQ */}
            <Section variant="gray">
                <SectionHeader eyebrow="FAQ" title="Najczęściej zadawane pytania" />
                <div className="max-w-3xl mx-auto">
                    <Accordion type="single" collapsible data-testid="faq-accordion">
                        {faqs.map((f, i) => (
                            <AccordionItem key={i} value={`q-${i}`} className="border-b border-[#E5E7EB]">
                                <AccordionTrigger className="text-left text-base md:text-lg font-semibold text-[#0B1220] hover:no-underline hover:text-[#14B8A6] py-5" data-testid={`faq-q-${i}`}>
                                    {f.q}
                                </AccordionTrigger>
                                <AccordionContent className="text-[#0B1220]/70 text-[15px] leading-relaxed pb-5">
                                    {f.a}
                                </AccordionContent>
                            </AccordionItem>
                        ))}
                    </Accordion>
                </div>
            </Section>

            {/* Final CTA */}
            <section className="relative bg-[#06111F] text-white py-20 md:py-28 overflow-hidden">
                <div className="absolute inset-0 bg-grid-faint opacity-50" />
                <div className="absolute -top-20 left-1/2 -translate-x-1/2 w-[700px] h-[400px] rounded-full bg-[#14B8A6]/15 blur-3xl" />
                <div className="relative max-w-4xl mx-auto px-5 md:px-8 text-center">
                    <h2 className="font-display font-bold text-[30px] md:text-[40px] lg:text-[48px] leading-tight tracking-tight">
                        Twoja firma może docierać dalej.<br />
                        <span className="text-[#14B8A6]">Zacznijmy budować jej wizerunek w sieci.</span>
                    </h2>
                    <p className="mt-5 text-[#AAB6C5] text-base md:text-lg max-w-2xl mx-auto">
                        Opowiedz nam o swoim biznesie. Podpowiemy, jakie działania najlepiej wesprą jego rozwój.
                    </p>
                    <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
                        <PrimaryButton to="/kontakt" testid="final-cta-consult">Umów bezpłatną konsultację</PrimaryButton>
                        <SecondaryButton href={`tel:${site.phoneLink}`} light testid="final-cta-call">
                            Zadzwoń: {site.phoneDisplay}
                        </SecondaryButton>
                    </div>
                    <p className="mt-6 text-[#AAB6C5] text-sm">
                        lub napisz na <a href={`mailto:${site.email}`} className="text-[#14B8A6] hover:underline">{site.email}</a>
                    </p>
                </div>
            </section>
        </>
    );
}
